import { getBackupRequiredValidator } from "integrations/dromo/accountSchemaUtils";
import { DROMO_DATE_FORMAT } from "integrations/dromo/dromoFormats";
import {
  getDebtorRequiredTogetherFields,
  getDebtorsDataFields,
} from "integrations/dromo/debtorSchemaFields";

export default function getAccountSchemaFields(
  accountType,
  requiredBackupFields = [],
  dataImportType = null,
) {
  const fields = [
    {
      key: "client_reference_id",
      label: "Client Reference ID",
      type: "string",
      validators:
        dataImportType === "account_consumer_upsert"
          ? [{ validate: "unique_case_insensitive" }]
          : [],
    },
    {
      key: "assignee_id",
      label: "Assignee ID",
      type: "number",
    },
    {
      key: "principal",
      label: "Original Principal",
      type: ["number", "usd"],
      validators: [{ validate: "required" }],
    },
    {
      key: "interest",
      label: "Original Agency Interest",
      type: ["number", "usd"],
    },
    {
      key: "client_interest",
      label: "Original Client Interest",
      type: ["number", "usd"],
    },
    {
      key: "prejudgment_interest",
      label: "Original Prejudgment Interest",
      type: ["number", "usd"],
    },
    {
      key: "total_previous_credits_at_turnover",
      label: "[NEW] - Total Previous Payments & Credits",
      description:
        'The total amount of all previous payments and credits prior to the turnover date including the "Last Payment Amount". This field will be subtracted from the total original balance, and pay towards the principal payment amount so must be strictly less than the original principal balance. If this field is used in combination with "Last Payment Amount", the "Last Payment Amount" will only be subtracted from this field so as to not account for the last payment twice towards the balance.',
      type: ["number", "usd"],
    },
    {
      key: "last_payment_amount_at_turnover",
      label: "[NEW] - Last Payment Amount",
      description: "The last payment amount paid by the consumer prior to the turnover date.",
      type: ["number", "usd"],
    },
    {
      key: "last_payment_date_at_turnover",
      label: "Last Payment Date",
      description: "The date of the last payment made by the consumer prior to the turnover date.",
      type: DROMO_DATE_FORMAT,
      // https://developer.dromo.io/fields/#require-with-other-field-values
      validators: [
        {
          validate: "require_with",
          fields: ["last_payment_amount_at_turnover"],
          errorMessage:
            'The "Last Payment Date" is required if the "Last Payment Amount" is specified.',
        },
      ].concat(getBackupRequiredValidator("last_payment_date", requiredBackupFields)),
    },
    {
      key: "interest_rate",
      label: "Interest Rate (e.g '19.99%')",
      type: ["number", "percent_2"],
      validators: [
        { validate: "require_with", fields: ["interest_start_date", "interest_end_date"] },
      ],
      description:
        "Interest rates must be entered as a decimal (e.g '0.20'), or a number with the percentage sign (e.g '20%')",
    },
    {
      key: "date_of_service",
      label: "Date of Service",
      type: DROMO_DATE_FORMAT,
      description: "The date the service was performed (e.g a doctor's visit).",
      validators: getBackupRequiredValidator("date_of_service", requiredBackupFields),
    },
    {
      key: "itemization_date",
      label: "Itemization Date",
      type: DROMO_DATE_FORMAT,
      description: "The date that appears on the Model Validation Notice.",
      validators: getBackupRequiredValidator("itemization_date", requiredBackupFields),
    },
    {
      key: "judgment_date",
      label: "Judgement Date",
      type: DROMO_DATE_FORMAT,
    },
    {
      key: "date_of_first_delinquency",
      label: "Date of First Delinquency",
      type: DROMO_DATE_FORMAT,
      description:
        "The date the consumer first missed a payment. Required for Credit Reporting via Metro2.",
      validators: getBackupRequiredValidator("date_of_first_delinquency", requiredBackupFields),
    },
    {
      key: "interest_start_date",
      label: "Interest Start Date",
      type: DROMO_DATE_FORMAT,
      validators: getBackupRequiredValidator("interest_start_date", requiredBackupFields, [
        { validate: "require_with", fields: ["interest_rate", "interest_end_date"] },
      ]),
    },
    {
      key: "interest_end_date",
      label: "Interest End Date",
      type: DROMO_DATE_FORMAT,
    },
    {
      key: "turnover_date",
      label: "Turnover Date",
      type: DROMO_DATE_FORMAT,
      description:
        "The date the account was transferred / 'turned over' to the collection agency, or debt buyer.",
      validators: getBackupRequiredValidator("turnover_date", requiredBackupFields),
    },
    {
      key: "open_date",
      label: "Open Date",
      type: DROMO_DATE_FORMAT,
      description: "The date the account was opened.",
      validators: getBackupRequiredValidator("open_date", requiredBackupFields),
    },
    {
      key: "last_statement_date",
      label: "Last Statement Date",
      type: DROMO_DATE_FORMAT,
      description:
        "The date the creditor issued the most recent account statement to the consumer. Establishes the last formally communicated balance before the account went into collections.",
      validators: getBackupRequiredValidator("last_statement_date", requiredBackupFields),
    },
    {
      key: "charge_off_date",
      label: "Charge Off Date",
      type: DROMO_DATE_FORMAT,
      description:
        "The date the original creditor (usually a bank/creditor) writes the account off as a loss for accounting purposes.",
      validators: getBackupRequiredValidator("charge_off_date", requiredBackupFields),
    },
    {
      key: "statute_of_limitations_expiry_date",
      label: "Out Of Statute Date",
      type: DROMO_DATE_FORMAT,
      description: "The date the statute of limitations expires for the account.",
    },
    ...getDebtorsDataFields(3, accountType),
    {
      key: "special_comment_code",
      label: "Special Comment Code",
      type: "select",
      selectOptions: [
        { label: "B - Payments Managed by Credit Counseling Service", value: "B" },
        { label: "C - Paid by Co-Maker", value: "C" },
        { label: "M - Account Closed at Credit Grantor's Request", value: "M" },
        { label: "O - Account Transferred to Another Lender", value: "O" },
        { label: "S - Special Handling", value: "S" },
        { label: "V - Adjustment Pending", value: "V" },
        { label: "AB - Debt Being Paid Through Insurance", value: "AB" },
        { label: "AC - Paying Under a Partial Payment Agreement", value: "AC" },
        { label: "AH - Purchased by Another Lender", value: "AH" },
        { label: "AI - Recalled to Active Military Duty", value: "AI" },
        { label: "AJ - Payroll Deduction", value: "AJ" },
        { label: "AM - Account Payment Assured by Wage Garnishment", value: "AM" },
        { label: "AN - Account Acquired by RTC/FDIC", value: "AN" },
        { label: "AP - Credit Line Suspended", value: "AP" },
        { label: "AR - Contingent Liability", value: "AR" },
        { label: "AS - Account Closed Due to Refinance", value: "AS" },
        { label: "AT - Account Closed Due to Transfer", value: "AT" },
        { label: "AU - Account Paid in Full for Less than Full Balance", value: "AU" },
        { label: "AV - First Payment Never Received", value: "AV" },
        { label: "AW - Affected by Natural Disaster", value: "AW" },
        { label: "BA - Transferred to Recovery", value: "BA" },
        { label: "BL - Credit Card Lost or Stolen", value: "BL" },
        { label: "BP - Paid through Insurance", value: "BP" },
        { label: "BZ - Judgment Granted", value: "BZ" },
        { label: "CS - For Use by Child Support Agencies Only", value: "CS" },
      ],
    },
    {
      key: "compliance_condition_code",
      label: "Compliance Condition Code",
      type: "select",
      selectOptions: [
        { label: "XA - Account closed at consumer's request", value: "XA" },
        { label: "XB - Account information disputed by consumer", value: "XB" },
        { label: "XC - Completed investigation of FCRA", value: "XC" },
        { label: "XD - Closed at consumer's request & in dispute under FCRA", value: "XD" },
        {
          label: "XE - Closed at cons request, dispute completed, consumer disagrees",
          value: "XE",
        },
        { label: "XF - Account in dispute under Fair Credit Billing Act", value: "XF" },
        { label: "XG - FCBA Dispute resolved (consumer disagrees)", value: "XG" },
        {
          label: "XH - Previously in dispute (now resolved, reported by credit grantor)",
          value: "XH",
        },
        { label: "XR - Removes the most recently reported Compliance Condition Code", value: "XR" },
      ],
    },
    {
      key: "bcii_code",
      label: "BCII Code",
      type: "select",
      selectOptions: [
        { label: "A - Petition for Chapter 7 Bankruptcy", value: "A" },
        { label: "B - Petition for Chapter 11 Bankruptcy", value: "B" },
        { label: "C - Petition for Chapter 12 Bankruptcy", value: "C" },
        { label: "D - Petition for Chapter 13 Bankruptcy", value: "D" },
        { label: "E - Discharged through Bankruptcy Chapter 7", value: "E" },
        { label: "F - Discharged through Bankruptcy Chapter 11", value: "F" },
        { label: "G - Discharged through Bankruptcy Chapter 12", value: "G" },
        { label: "H - Discharged through Bankruptcy Chapter 13", value: "H" },
        { label: "I - Chapter 7 Bankruptcy Dismissed", value: "I" },
        { label: "J - Chapter 11 Bankruptcy Dismissed", value: "J" },
        { label: "K - Chapter 12 Bankruptcy Dismissed", value: "K" },
        { label: "L - Chapter 13 Bankruptcy Dismissed", value: "L" },
        { label: "M - Chapter 7 Bankruptcy Withdrawn", value: "M" },
        { label: "N - Chapter 11 Bankruptcy Withdrawn", value: "N" },
        { label: "O - Chapter 12 Bankruptcy Withdrawn", value: "O" },
        { label: "P - Chapter 13 Bankruptcy Withdrawn", value: "P" },
        { label: "Q - Removes previously reported Bankruptcy Indicator", value: "Q" },
        { label: "R - Reaffirmation of Debt", value: "R" },
        { label: "S - Removes previously reported Reaffirmation of Debt", value: "S" },
        { label: "T - Credit Grantor Cannot Locate Consumer", value: "T" },
        { label: "U - Consumer Now Located", value: "U" },
        { label: "V - Chapter 7 Reaffirmation of Debt Rescinded", value: "V" },
        { label: "Z - Bankruptcy (Undesignated Chapter)", value: "Z" },
        { label: "1A - Personal Receivership", value: "1A" },
        { label: "2A - Lease Assumption", value: "2A" },
      ],
    },
    {
      key: "ecoa_code",
      label: "ECOA Code",
      type: "select",
      selectOptions: [
        {
          label:
            "1 - Individual: This consumer has contractual responsibility for this account and is primarily responsible for its payment.",
          value: "1",
        },
        {
          label:
            "2 - Joint Contractual Liability: This consumer has contractual responsibility for this joint account.",
          value: "2",
        },
        {
          label:
            "3 - Authorized User: This consumer is an authorized user of this account; another consumer has contractual responsibility.",
          value: "3",
        },
        {
          label:
            "5 - Co-Maker or Guarantor: This consumer is the co-maker or guarantor for this account, who becomes liable if the maker defaults.",
          value: "5",
        },
        {
          label:
            "7 - Maker: This consumer is the maker who is liable for the account, but a co-maker or guarantor is liable if the maker defaults.",
          value: "7",
        },
        {
          label:
            "T - Terminated: The association with the account has been terminated by this consumer.",
          value: "T",
        },
        {
          label: "X - Deceased: - This consumer is deceased.",
          value: "X",
        },
        {
          label:
            "W - Business/Commercial: This code is used to identify that the company reported in the Name field is contractually liable for this account.",
          value: "W",
        },
        {
          label: "Z - Delete Consumer: This code is used to delete this consumer from the account.",
          value: "Z",
        },
      ],
    },
    {
      key: "credit_reporting_status",
      label: "Credit Reporting Status",
      type: "select",
      selectOptions: [
        { label: "U - Being Reported", value: "U" },
        { label: "R - Being Reported", value: "R" },
        { label: "S - Closed", value: "S" },
        { label: "N - Never Been Reported", value: "N" },
        { label: "H - Suppressed", value: "H" },
        { label: "C - Will Be Closed", value: "C" },
      ],
    },
    {
      key: "credit_bureau_number",
      label: "Credit Bureau Number",
      type: "string",
    },
    {
      key: "is_skip_credit_reporting",
      label: "Skip Credit Reporting",
      type: "checkbox",
    },
    {
      key: "credit_bureau_notes",
      label: "Credit Bureau Notes",
      type: "string",
    },
    {
      key: "current_creditor_name",
      label: "Current Creditor Name",
      type: "string",
    },
    {
      key: "original_creditor_name",
      label: "Original Creditor Name",
      type: "string",
    },
    {
      key: "legal_fees",
      label: "Original Legal Fees",
      type: ["number", "usd"],
    },
    {
      key: "check_fees",
      label: "Original Check Fees",
      type: ["number", "usd"],
    },
    {
      key: "collection_fees",
      label: "Original Collection Fees",
      type: ["number", "usd"],
    },
    {
      key: "misc_fees",
      label: "Original Misc Fees",
      type: ["number", "usd"],
    },
    {
      key: "attorney_fees",
      label: "Original Attorney Fees",
      type: ["number", "usd"],
    },
    {
      key: "treble_damages",
      label: "Original Treble Damages",
      type: ["number", "usd"],
    },
    {
      key: "handling_fees",
      label: "Original Handling Fees",
      type: ["number", "usd"],
    },
    {
      key: "court_fees",
      label: "Original Court Fees",
      type: ["number", "usd"],
    },
    {
      key: "service_fees",
      label: "Original Service Fees",
      type: ["number", "usd"],
    },
    {
      key: "lien_fees",
      label: "Original Lien Fees",
      type: ["number", "usd"],
    },
    {
      key: "client_fees",
      label: "Original Client Fees",
      type: ["number", "usd"],
    },
    {
      key: "garnishment_fees",
      label: "Original Garnishment Fees",
      type: ["number", "usd"],
    },
    {
      key: "postjudgment_attorney_fees",
      label: "Original Postjudgment Attorney Fees",
      type: ["number", "usd"],
    },
    {
      key: "external_score",
      label: "Account Score (e.g '999.99')",
      type: ["number", "decimal_2"],
      description:
        "The internal collectability score used by the collection agency to determine an account's priority for collection.",
    },
  ];
  // apply required together validation to fields
  fields.forEach((field) => {
    getDebtorRequiredTogetherFields(3).forEach((fieldSet) => {
      if (fieldSet.has(field.key)) {
        (field.validators ??= []).push({
          validate: "require_with",
          fields: Array.from(fieldSet).filter((key) => key !== field.key),
        });
      }
    });
  });
  return fields;
}
